import { routes } from "@bezoned/shared/routes.js";
import type React from "react";
import { createContext, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { FluidErrorScreen } from "../../components/ui/fluid-error-screen.js";
import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { updateSearchParams } from "../../utils/url-search-params.js";
import { useOfficeExit } from "../office/use-office-exit.js";
import type {
  FluidContainer,
  FluidServices,
  FluidSharedTree,
} from "./fluid.client.js";
import { RedirectToLobbyError } from "./fluid.errors.js";
import { useContainerMigration } from "./hooks/use-container-migration.js";
import { useFluidConnectionManager } from "./hooks/use-fluid-connection-manager.js";
import { useFluidDeduplication } from "./hooks/use-fluid-deduplication.js";

type FluidContextType =
  | {
      __ctx_init: false;
      container: null;
      containerId: null;
      services: null;
      sharedTree: null;
      fluidUserId: null;
      connectionState: "connecting";
      retry: null;
    }
  | {
      __ctx_init: true;
      container: FluidContainer;
      containerId: string;
      services: FluidServices;
      sharedTree: FluidSharedTree;
      fluidUserId: string;
      connectionState: "connecting" | "connected" | "disconnected";
      retry: () => void;
    };

export const FluidContext = createContext<FluidContextType>({
  __ctx_init: false,
  connectionState: "connecting",
  container: null,
  containerId: null,
  fluidUserId: null,
  retry: null,
  services: null,
  sharedTree: null,
});

const FluidDeduplicationGuard = () => {
  useFluidDeduplication();
  return null;
};

const OfficeExitGuard = () => {
  useOfficeExit();
  return null;
};

export const FluidProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setModalVariant } = useLayoutStore();
  const {
    container,
    containerId,
    services,
    sharedTree,
    fluidUserId,
    connectionState,
    retry,
    error,
    isRetrying,
  } = useFluidConnectionManager();

  const handleOutdatedContainer = useCallback(() => {
    setModalVariant("outdated_office");
  }, [setModalVariant]);

  useContainerMigration({
    onOutdated: handleOutdatedContainer,
    sharedTree,
  });

  useEffect(() => {
    if (error instanceof RedirectToLobbyError) {
      const errorCode = error.errorCode || "unknown";
      navigate(
        {
          pathname: routes.protected.chooseTeam,
          search: updateSearchParams(location.search, {
            remove: ["teamId"],
            set: { error: errorCode },
          }),
        },
        { replace: true }
      );
    }
  }, [error, location.search, navigate]);

  const contextValue = useMemo<FluidContextType>(() => {
    if (container && containerId && sharedTree && services && fluidUserId) {
      return {
        __ctx_init: true,
        connectionState,
        container,
        containerId,
        fluidUserId,
        retry,
        services,
        sharedTree,
      };
    }

    return {
      __ctx_init: false,
      connectionState: "connecting",
      container: null,
      containerId: null,
      fluidUserId: null,
      retry: null,
      services: null,
      sharedTree: null,
    };
  }, [
    container,
    containerId,
    sharedTree,
    services,
    connectionState,
    fluidUserId,
    retry,
  ]);

  if (error && !(error instanceof RedirectToLobbyError)) {
    return (
      <FluidErrorScreen error={error} isRetrying={isRetrying} onRetry={retry} />
    );
  }

  if (!(container && containerId && sharedTree && fluidUserId)) {
    return <FullScreenLoader message="Entering office" />;
  }

  return (
    <FluidContext.Provider value={contextValue}>
      <FluidDeduplicationGuard />
      <OfficeExitGuard />
      {children}
    </FluidContext.Provider>
  );
};
